import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../provider/AuthProvider";
import { useLoginModal } from "../../context/LoginModalContext";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { VELLUM as T, SANS, display, mono, body, btn } from "../shared/folio/kit";
import { FP, SheetTitle, FieldBlock, FieldRow, CheckRow, ErrorNote, fieldInput } from "../shared/folio/form";

/* Sign in, drawn as a sheet.

   The fields are a title block: mono label, hairline, value cell - the same
   apparatus the register uses for its own figures, so a form and a schedule
   are read the same way. */

export const DIALOG_TITLE_ID = "auth-dialog-title";

const header: React.CSSProperties = {
  padding: "34px 32px 22px",
  borderBottom: `1.5px solid ${T.ink}`,
};

const bodyPad: React.CSSProperties = { padding: "26px 32px 32px" };

export const LoginModalContent: React.FC = () => {
  const navigate = useNavigate();
  const { closeLoginModal, switchToRegister } = useLoginModal();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const { login, loading, user } = useAuth();

  useEffect(() => {
    if (user) {
      closeLoginModal();
      if (user.role === "employee") navigate(ROUTE_NAMES.PAGES.JOB_SEEKER_DASHBOARD);
      else if (user.role === "employer") navigate(ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD);
      else navigate(ROUTE_NAMES.PAGES.HOME);
    }
  }, [user, navigate, closeLoginModal]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const result = await login(email, password, undefined, rememberMe);
      if (result.requiresRoleSelection && result.roles) {
        setAvailableRoles(result.roles);
        setShowRoleSelection(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed. Please try again.");
    }
  };

  const handleRoleSelect = async (role: "employee" | "employer") => {
    setError("");
    try {
      await login(email, password, role, rememberMe);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed. Please try again.");
      setShowRoleSelection(false);
    }
  };

  if (showRoleSelection) {
    return (
      <div style={{ fontFamily: SANS, color: T.ink }}>
        <div style={header}>
          <SheetTitle
            id={DIALOG_TITLE_ID}
            code="§ choose account"
            title="Pick a role."
            standfirst="Two accounts share this email. Which would you like to sign into?"
          />
        </div>

        <div style={{ ...bodyPad, display: "grid", gap: 20 }}>
          {error && <ErrorNote>{error}</ErrorNote>}

          <div style={{ border: `1.5px solid ${T.ink}`, background: T.ground }}>
            {availableRoles.includes("employee") && (
              <RoleCell
                first
                code="A · Scholar"
                title="Job seeker"
                note="Find and apply for academic positions"
                disabled={loading}
                onClick={() => handleRoleSelect("employee")}
              />
            )}
            {availableRoles.includes("employer") && (
              <RoleCell
                first={!availableRoles.includes("employee")}
                code="B · Institution"
                title="Employer"
                note="Post jobs and manage applications"
                disabled={loading}
                onClick={() => handleRoleSelect("employer")}
              />
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setShowRoleSelection(false);
              setAvailableRoles([]);
            }}
            className={`${FP}-quiet`}
            style={{
              ...mono(T),
              background: "transparent",
              border: "none",
              borderBottom: `1px solid ${T.rule}`,
              padding: "0 0 3px",
              cursor: "pointer",
              justifySelf: "center",
            }}
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: SANS, color: T.ink }}>
      <div style={header}>
        <SheetTitle
          id={DIALOG_TITLE_ID}
          code="§ sign in"
          title="Welcome back."
          standfirst="Resume where you left off - saved searches, applications and digest preferences."
        />
      </div>

      <form onSubmit={handleLogin} style={{ ...bodyPad, display: "grid", gap: 20 }}>
        {error && <ErrorNote>{error}</ErrorNote>}

        <div>
          <FieldBlock>
            <FieldRow label="Email" htmlFor="login-email" required first>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@university.ac.uk"
                style={fieldInput}
              />
            </FieldRow>
            <FieldRow label="Password" htmlFor="login-password" required>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="•••••••••"
                style={fieldInput}
              />
            </FieldRow>
          </FieldBlock>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button
              type="button"
              onClick={() => {
                closeLoginModal();
                navigate(ROUTE_NAMES.PAGES.FORGOT_PASSWORD);
              }}
              className={`${FP}-quiet`}
              style={{
                ...mono(T),
                background: "transparent",
                border: "none",
                borderBottom: `1px solid ${T.rule}`,
                padding: "0 0 3px",
                cursor: "pointer",
              }}
            >
              Forgot your password?
            </button>
          </div>
        </div>

        <CheckRow checked={rememberMe} onChange={setRememberMe}>
          Keep me signed in on this device
        </CheckRow>

        <button
          type="submit"
          disabled={loading}
          className={`${FP}-btn`}
          style={{ ...btn(T, "accent"), width: "100%", justifyContent: "center" }}
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <div
          style={{
            borderTop: `1px solid ${T.rule}`,
            paddingTop: 18,
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
            fontFamily: SANS,
            fontSize: 13.5,
            color: T.mute,
          }}
        >
          New to Acanetic?
          <button
            type="button"
            onClick={switchToRegister}
            className={`${FP}-quiet`}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: `1px solid ${T.ink}`,
              padding: "0 0 2px",
              cursor: "pointer",
              fontFamily: SANS,
              fontSize: 13.5,
              fontWeight: 600,
              color: T.ink,
            }}
          >
            Create an account →
          </button>
        </div>
      </form>
    </div>
  );
};

const RoleCell: React.FC<{
  code: string;
  title: string;
  note: string;
  disabled?: boolean;
  first?: boolean;
  onClick: () => void;
}> = ({ code, title, note, disabled, first, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`${FP}-role`}
    style={{
      width: "100%",
      display: "block",
      textAlign: "left",
      background: "transparent",
      border: "none",
      borderTop: first ? "none" : `1px solid ${T.rule}`,
      borderRadius: 0,
      padding: "16px 18px",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      fontFamily: SANS,
    }}
  >
    <span style={{ ...mono(T, T.accent), display: "block" }}>{code}</span>
    <span style={{ ...display(T, "19px"), display: "block", marginTop: 6 }}>{title}</span>
    {/* Prose, so it is set in the sans. Mono here would be a costume. */}
    <span style={{ ...body(T), fontSize: 12.5, display: "block", marginTop: 3 }}>{note}</span>
  </button>
);
